import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import ExploreStep from './ExploreStep';
import StepTab from './StepTab';
import SelectRoomsStep from './SelectRoomsStep';
import CostsStep from './CostsStep';
import DropdownInput from '../../components/DropdownInput';
import IncentiveFiltersModal from './IncentiveFiltersModal';
import useQueryParams from '../../hooks/useQueryParams';
import NextStepsStep from './NextStepsStep';
import DetailsDrawer from '../../components/DetailsDrawer';
import useDetailsDrawer from '../../hooks/useDetailsDrawer';
import BillImpactStep from './BillImpactStep';

export default function StepWizard({
  room,
  rooms,
  myProject,
  incentives,
  filters,
  productsByRating,
  resources,
  incentiveImgix,
}) {
  const { setQueryParams } = useQueryParams();
  const [incentiveFiltersModalIsOpen, setIncentiveFiltersModalIsOpen] = useState(false);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [currentStep, setCurrentStep] = useState(isSmallScreen ? 1 : 0);

  // When the viewport shrinks to mobile while the user is on the Explore step (index 0),
  // advance to the first visible step. We intentionally omit currentStep from deps because
  // we only want to react to breakpoint changes, not every step change.
  useEffect(() => {
    if (isSmallScreen && currentStep === 0) {
      setCurrentStep(1);
    }
  }, [isSmallScreen]);
  const tabRefs = useRef([]);
  const panelRef = useRef(null);

  const {
    isOpen: isResourceDrawerOpen,
    onOpen: buildResourceDrawerClickHandler,
    onClose: onCloseResourceDrawer,
    detailItem: resource,
  } = useDetailsDrawer('resource', 'resource_id');

  const {
    isOpen: isProductDrawerOpen,
    onOpen: buildProductDrawerClickHandler,
    onClose: onCloseProductDrawer,
    detailItem: product,
  } = useDetailsDrawer();

  const setMyProject = (rooms) => {
    setQueryParams({
      my_project: rooms.map((room) => room.handle),
    });
  };

  const toggleRoom = (room) => {
    const roomInMyProject = myProject.some((r) => r.handle === room.handle);
    if (roomInMyProject) {
      // Remove the room from my project
      setMyProject(myProject.filter((r) => r.handle !== room.handle));
    } else {
      // Add the room to my project
      setMyProject([...myProject, room]);
    }
  };

  const goToStep = useCallback((index) => {
    setCurrentStep(index);
  }, []);

  const handleNext = useCallback(() => {
    const next = currentStep + 1;
    setCurrentStep(next);
    // Move focus to the panel so screen readers announce the new content
    panelRef.current?.focus();
  }, [currentStep]);

  const handleBack = useCallback(() => {
    const prev = currentStep - 1;
    setCurrentStep(prev);
    panelRef.current?.focus();
  }, [currentStep]);

  const STEPS = [
    {
      id: 'explore',
      label: 'Explore Electrification',
      content: <ExploreStep rooms={rooms} onNext={handleNext} />,
      hideOnSmallScreens: true,
    },
    {
      id: 'rooms',
      label: `Select Appliances (${myProject.length})`,
      content: (
        <SelectRoomsStep
          onNext={handleNext}
          rooms={rooms}
          myProject={myProject}
          toggleRoom={toggleRoom}
        />
      ),
    },
    {
      id: 'costs',
      label: 'Product Costs',
      disableUntilRoomsSelected: true,
      content: (
        <CostsStep
          myProject={myProject}
          incentives={incentives}
          onBack={handleBack}
          onNext={handleNext}
          onClickIncentivePrompt={() => setIncentiveFiltersModalIsOpen(true)}
        />
      ),
    },
    {
      id: 'bill-impact',
      label: 'Bill Impact',
      disableUntilRoomsSelected: true,
      content: <BillImpactStep myProject={myProject} onBack={handleBack} onNext={handleNext} />,
    },
    {
      id: 'next-steps',
      label: 'Next Steps',
      disableUntilRoomsSelected: true,
      content: (
        <NextStepsStep
          myProject={myProject}
          onBack={handleBack}
          onEditSelections={() => goToStep(1)}
          room={room}
          productsByRating={productsByRating}
          buildProductDrawerClickHandler={buildProductDrawerClickHandler}
          buildResourceDrawerClickHandler={buildResourceDrawerClickHandler}
          incentives={incentives}
          resources={resources}
          incentiveImgix={incentiveImgix}
        />
      ),
    },
  ];

  return (
    <Box width="100%" mt={4}>
      {/* Tab nav */}
      <Box
        display={{ xs: 'none', sm: 'flex' }}
        role="tablist"
        aria-label="Electrification planning steps"
      >
        {STEPS.map((step, index) => (
          <StepTab
            key={step.id}
            step={step}
            index={index}
            isActive={currentStep === index}
            isDisabled={step.disableUntilRoomsSelected && myProject.length === 0}
            isHidden={step.hideOnSmallScreens && isSmallScreen}
            onClick={() => goToStep(index)}
            tabRef={(el) => {
              tabRefs.current[index] = el;
            }}
          />
        ))}
      </Box>

      {/* Mobile dropdown nav — excludes steps hidden on small screens */}
      <Box display={{ xs: 'flex', sm: 'none' }} px={2} pt={2} bgcolor="common.white">
        <DropdownInput
          id="electrify-step-select"
          ariaLabel="Select step"
          options={STEPS.map((step, idx) => ({
            hidden: step.hideOnSmallScreens,
            value: idx,
            children: step.label,
            disabled: step.disableUntilRoomsSelected && myProject.length === 0,
          })).filter((opt) => !opt.hidden)}
          value={currentStep}
          fullWidth
          onChange={(e) => goToStep(parseInt(e.target.value))}
          sx={{
            '& .MuiSelect-select': { fontWeight: 700, color: 'blue.500' },
          }}
          menuStyles={{
            '& .MuiMenu-paper': { width: '120px', minWidth: 'auto' },
            '& .MuiMenuItem-root': { fontWeight: 700, color: 'blue.500' },
          }}
        />
      </Box>

      {/* Tab panels */}
      {STEPS.map((step, index) => (
        <Box
          key={step.id}
          role="tabpanel"
          id={`electrify-panel-${step.id}`}
          aria-labelledby={`electrify-tab-${step.id}`}
          hidden={currentStep !== index}
          ref={currentStep === index ? panelRef : null}
          tabIndex={-1}
          bgcolor="white"
          p={{ xs: 2, sm: 4 }}
          sx={{
            '&:focus': { outline: 'none' },
          }}
        >
          {currentStep === index && <>{step.content}</>}
        </Box>
      ))}

      {/* Live region: announces step changes to screen readers without stealing focus */}
      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
        }}
      >
        {(() => {
          const visibleSteps = STEPS.filter((s) => !isSmallScreen || !s.hideOnSmallScreens);
          const visibleIndex = visibleSteps.findIndex((s) => s.id === STEPS[currentStep].id);
          return `Step ${visibleIndex + 1} of ${visibleSteps.length}: ${STEPS[currentStep].label}`;
        })()}
      </Box>

      <DetailsDrawer
        isOpen={isResourceDrawerOpen}
        onClose={onCloseResourceDrawer}
        detailItem={resource}
        isProduct={false}
        onShowProduct={buildProductDrawerClickHandler}
      />

      <DetailsDrawer
        isOpen={isProductDrawerOpen}
        onClose={onCloseProductDrawer}
        detailItem={product}
        onShowResource={buildResourceDrawerClickHandler}
      />

      <IncentiveFiltersModal
        open={incentiveFiltersModalIsOpen}
        onClose={() => {
          setIncentiveFiltersModalIsOpen(false);
        }}
        filters={filters}
        onUpdate={(filters) => {
          setQueryParams(filters);
          setIncentiveFiltersModalIsOpen(false);
        }}
      />
    </Box>
  );
}
